import React, { useCallback } from 'react';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { IconShareSvg } from 'apps/sigma2-ui/src/components/svg/IconShare';
import { IconDownloadSvg } from 'apps/sigma2-ui/src/components/svg/IconDownload';
import { PayBenchmarkingLevel } from 'libs/end-to-end/src/pay-benchmarking';
import { PayBenchmarkingOutput } from 'libs/end-to-end/src/pay-benchmarking';
import { formatPriceAmountToString } from 'libs/end-to-end/src/currencies';
import { PayBenchmarkingEmptyStateIconSvg } from 'apps/sigma2-ui/src/components/svg/PayBenchmarkingEmptyStateIcon';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { PayBenchmarkingCompensationTotalCompView } from 'apps/sigma2-ui/src/pay-benchmarking/components/PayBenchmarkingCompensationTotalCompView';
import { usePayBenchmarking } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarking';
import { PayBenchmarkingCompensationGraphView } from 'apps/sigma2-ui/src/pay-benchmarking/components/PayBenchmarkingCompensationGraphView';
import { PayBenchmarkingCompensationTileView } from 'apps/sigma2-ui/src/pay-benchmarking/components/PayBenchmarkingCompensationTileView';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';

export const PayBenchmarkingInfoResultsView = (props: {
  organizationId: string;
  onDownload: () => void;
  onShare: (link: string) => void;
}) => {
  const { currentState, generateShareLink } = usePayBenchmarking();

  const onShare = useCallback(() => {
    generateShareLink(props.onShare);
  }, [generateShareLink, props.onShare]);

  const { onUserAnalysisCancel } = usePayBenchmarking();

  const mainUi = useCallback(
    (output: PayBenchmarkingOutput) => {
      const sortedComparisonLevels = output.comparisonLevels.sort((a, b) => {
        return a.percentileSalary50 - b.percentileSalary50;
      });

      return (
        <div>
          <div className="p-6">
            <div className=" flex justify-between items-center gap-2 pb-2">
              <Heading level={2} textColor={'text-black'}>
                {output.appliedFilters.role}
              </Heading>
              <div className="flex items-center gap-2">
                <button onClick={onShare}>
                  <IconShareSvg svg={{ className: 'w-6 h-6 text-secondary' }} />
                </button>
                <button onClick={props.onDownload}>
                  <IconDownloadSvg
                    svg={{ className: 'w-6 h-6 text-secondary' }}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="bg-gray-100 pb-4">
            <div className="p-6">
              <Heading level={4} textColor={'text-black'}>
                Compensation
              </Heading>

              <PayBenchmarkingCompensationTotalCompView
                currency={output.appliedFilters.currency}
                compensationUnit={output.appliedFilters.compensationUnit}
                totalCompensation={
                  output.compensationFigures.suggestedTotalCompensation
                }
                internalCompaRatio={
                  output.compensationFigures.internalCompaRatio
                }
              />

              <PayBenchmarkingCompensationGraphView
                currency={output.appliedFilters.currency}
                compensationFigures={output.compensationFigures}
                variant={'Details'}
                className={'h-[4rem]'}
              />

              <PayBenchmarkingCompensationTileView
                compensationFigures={output.compensationFigures}
                compensationUnit={output.appliedFilters.compensationUnit}
                currency={output.appliedFilters.currency}
              />
            </div>
          </div>

          <div className="bg-white pb-4">
            <div className="p-6">
              <Heading level={4} textColor={'text-black'} className="pb-4">
                Level Comparison
              </Heading>

              <Table
                items={sortedComparisonLevels}
                columnDefinitions={[
                  {
                    id: 'level',
                    header: () => <div>Level</div>,
                    cell: (params: { item: PayBenchmarkingLevel }) => (
                      <div>{params.item.name}</div>
                    ),
                  },
                  {
                    id: '25th',
                    header: () => <div>25th</div>,
                    cell: (params: { item: PayBenchmarkingLevel }) => (
                      <div>
                        {formatPriceAmountToString({
                          amount: params.item.percentileSalary25,
                          currency: output.appliedFilters.currency,
                          roundToWholeValue: true,
                        })}
                      </div>
                    ),
                  },
                  {
                    id: '50th',
                    header: () => <div>50th</div>,
                    cell: (params: { item: PayBenchmarkingLevel }) => (
                      <div>
                        {formatPriceAmountToString({
                          amount: params.item.percentileSalary50,
                          currency: output.appliedFilters.currency,
                          roundToWholeValue: true,
                        })}
                      </div>
                    ),
                  },
                  {
                    id: '75th',
                    header: () => <div>75th</div>,
                    cell: (params: { item: PayBenchmarkingLevel }) => (
                      <div>
                        {formatPriceAmountToString({
                          amount: params.item.percentileSalary75,
                          currency: output.appliedFilters.currency,
                          roundToWholeValue: true,
                        })}
                      </div>
                    ),
                  },
                  {
                    id: '90th',
                    header: () => <div>90th</div>,
                    cell: (params: { item: PayBenchmarkingLevel }) => (
                      <div>
                        {formatPriceAmountToString({
                          amount: params.item.percentileSalary90,
                          currency: output.appliedFilters.currency,
                          roundToWholeValue: true,
                        })}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      );
    },
    [onShare, props.onDownload],
  );

  const EmptyState = (
    <div className="h-full flex flex-col">
      <div className="flex-grow self-stretch" />
      <div className="flex flex-col justify-center items-center gap-2">
        <Heading level={3} textColor={'text-black'}>
          Get started by filtering
        </Heading>
        <Paragraph
          textColor={'text-black'}
          className="max-w-[30rem] text-center"
        >
          Use the filters to find recommended compensation based on industry
          benchmarks
        </Paragraph>
        <PayBenchmarkingEmptyStateIconSvg />
      </div>
      <div className="flex-grow self-stretch" />
    </div>
  );

  const ErrorState = (
    <div className="h-full flex flex-col">
      <div className="flex-grow self-stretch" />
      <div className="flex flex-col justify-center items-center gap-2">
        <Heading level={3} textColor={'text-black'}>
          Error
        </Heading>
        <Paragraph
          textColor={'text-black'}
          className="max-w-[30rem] text-center"
        >
          Please try adjusting your filters and try again
        </Paragraph>
      </div>
      <div className="flex-grow self-stretch" />
    </div>
  );

  const LoadingIndicator = (
    <div className="h-full flex flex-col">
      <div className="flex-grow self-stretch" />
      <div className="flex flex-col justify-center items-center gap-3">
        <Heading level={3} textColor={'text-black'}>
          Loading...
        </Heading>
        <CustomVizCircularSpinner onUserAnalysisCancel={onUserAnalysisCancel} />
      </div>
      <div className="flex-grow self-stretch" />
    </div>
  );

  return (
    <>
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />
      {currentState.state === 'loading_state' && LoadingIndicator}
      {currentState.state === 'error_state' && ErrorState}
      {currentState.state === 'empty_state' && EmptyState}
      {currentState.state === 'success' && mainUi(currentState.output)}
    </>
  );
};
